import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';
import { Button } from '@/shared/components/Button';
import { AlertCircle } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './FileUpload.module.scss';

export interface FileUploadProps {
  label: string;
  /** Tipos MIME ou extensoes aceitos. */
  accept?: string;
  /** Tamanho maximo em bytes. */
  maxSizeBytes?: number;
  onSelect: (file: File) => void;
  hint?: string;
  disabled?: boolean;
  /**
   * Esconde o botao "Escolher arquivo".
   *
   * A area inteira ja abre o seletor; onde o campo e pequeno — a foto do
   * paciente, por exemplo —, o botao repetia a mesma acao ocupando metade da
   * caixa.
   */
  compact?: boolean;
  /** Conteudo dentro da area, antes do rotulo. Ex.: a previa da imagem. */
  preview?: ReactNode;
  /** Acao ao pe da caixa, fora da area clicavel. Ex.: "Remover foto". */
  action?: ReactNode;
  className?: string;
}

const DEFAULT_MAX = 5 * 1024 * 1024;

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${Math.round(bytes / 1024 / 1024)} MB` : `${Math.round(bytes / 1024)} KB`;
}

/**
 * Selecao de arquivo, por clique ou por arrastar.
 *
 * A validacao de tipo e tamanho acontece antes de o arquivo subir: recusar no
 * cliente evita o upload inteiro para descobrir o erro no fim.
 *
 * Arrastar e conveniencia — o `<input type="file">` continua sendo o caminho
 * real, entao o componente e alcancavel por teclado sem depender do gesto.
 */
export function FileUpload({
  label,
  accept = 'image/jpeg,image/png,image/webp',
  maxSizeBytes = DEFAULT_MAX,
  onSelect,
  hint,
  disabled,
  compact,
  preview,
  action,
  className,
}: FileUploadProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = (file: File): boolean => {
    if (file.size > maxSizeBytes) {
      setError(`O arquivo tem ${formatSize(file.size)}. O limite é ${formatSize(maxSizeBytes)}.`);
      return false;
    }

    const accepted = accept.split(',').map((type) => type.trim());
    const matches = accepted.some((type) =>
      type.startsWith('.') ? file.name.toLowerCase().endsWith(type) : file.type === type,
    );

    if (!matches) {
      setError('Este formato de arquivo não é aceito aqui.');
      return false;
    }

    setError(null);
    return true;
  };

  const handleFile = (file: File | undefined): void => {
    if (!file) return;
    if (validate(file)) onSelect(file);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    handleFile(event.target.files?.[0]);
    // Permite reselecionar o mesmo arquivo depois de um erro.
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    handleFile(event.dataTransfer.files[0]);
  };

  return (
    <div className={cn(styles.root, className)}>
      {/*
        A area inteira e um `<label>`: clicar em qualquer ponto dela abre o
        seletor, sem depender de um botao. E comportamento nativo — nao ha
        `onClick` sintetico no meio do caminho — e o foco continua no `input`,
        que e o controle de verdade.
      */}
      <label
        htmlFor={inputId}
        className={cn(styles.dropzone, dragging && styles.dragging, disabled && styles.disabled)}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {preview ? <span className={styles.preview}>{preview}</span> : null}

        <span className={styles.label} id={`${inputId}-label`}>
          {label}
        </span>
        {hint ? (
          <span className={styles.hint} id={`${inputId}-hint`}>
            {hint}
          </span>
        ) : null}
        {compact ? null : (
          <Button variant="secondary" onClick={() => inputRef.current?.click()} disabled={disabled}>
            Escolher arquivo
          </Button>
        )}
        {/* O campo fica visualmente oculto, mas continua sendo o controle real. */}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          // O nome vem daqui, e nao do texto do `<label>` em volta: a area
          // inteira e rotulo e carrega tambem a dica e a previa, entao o texto
          // acumulado nomearia o campo com "Foto do paciente JPG, PNG...".
          aria-label={label}
          aria-describedby={hint ? `${inputId}-hint` : undefined}
          className={styles.input}
          onChange={handleChange}
          disabled={disabled}
        />
      </label>

      {action ? <div className={styles.action}>{action}</div> : null}

      {error ? (
        <p className={styles.error} role="alert">
          <Icon as={AlertCircle} size={16} /> {error}
        </p>
      ) : null}
    </div>
  );
}
